import { senhaSchema } from '@mymaxx/shared';
import { Aviso, Botao, CampoTexto } from '@mymaxx/ui';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ErroApi } from '../lib/api';
import { conviteApi } from '../lib/gestao-api';

/**
 * Tela pública do convite: quem recebeu o link define a própria senha.
 * O token é de uso único e vale 48 h.
 */
export function DefinirSenha() {
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const token = params.get('token') ?? '';

  const [convite, setConvite] = useState<{
    nome: string;
    email: string;
    exigeTotp: boolean;
  } | null>(null);
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!token) {
      setErro('Link inválido. Peça um novo ao administrador.');
      return;
    }
    conviteApi
      .validar(token)
      .then(setConvite)
      .catch((e) =>
        setErro(e instanceof ErroApi ? e.mensagem : 'Não foi possível validar este link.'),
      );
  }, [token]);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    const parse = senhaSchema.safeParse(senha);
    if (!parse.success) return setErro(parse.error.issues[0]?.message ?? 'Senha inválida');
    if (senha !== confirmacao) return setErro('As senhas não conferem');

    setEnviando(true);
    try {
      await conviteApi.definirSenha(token, senha, confirmacao);
      setPronto(true);
      setTimeout(() => navegar('/login', { replace: true }), 2500);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não foi possível definir a senha');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-card border border-neutral-200 p-6 dark:border-neutral-800">
        <img src="/logo.png" alt="MyMAXX" className="mx-auto size-14" width={56} height={56} />
        <h1 className="mb-4 text-xl font-semibold">Crie sua senha</h1>

        {pronto && <Aviso tipo="sucesso">Senha criada! Levando você para a tela de entrada…</Aviso>}

        {!pronto && !convite && erro && <Aviso tipo="erro">{erro}</Aviso>}
        {!pronto && !convite && !erro && (
          <p className="text-sm text-neutral-500">Validando link…</p>
        )}

        {!pronto && convite && (
          <form className="space-y-4" onSubmit={enviar} noValidate>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Olá, <strong>{convite.nome}</strong>. Defina a senha de acesso ao painel da MAXX
              Conectado.
            </p>
            {convite.exigeTotp && (
              <Aviso tipo="info">
                Seu perfil exige verificação em duas etapas. Depois de entrar, você vai configurar
                um aplicativo autenticador no celular — leve-o por perto.
              </Aviso>
            )}
            <CampoTexto
              rotulo="Nova senha"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              ajuda="Mínimo de 10 caracteres. Evite senhas usadas em outros sites."
            />
            <CampoTexto
              rotulo="Repita a senha"
              type="password"
              autoComplete="new-password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
            />
            {erro && <Aviso tipo="erro">{erro}</Aviso>}
            <Botao type="submit" className="w-full" carregando={enviando}>
              Salvar e continuar
            </Botao>
          </form>
        )}
      </div>
    </main>
  );
}
